import {
  createContext, ReactNode, SetStateAction,
  useMemo, useState, Dispatch, useContext, useEffect, useRef, MutableRefObject,
} from 'react';
import { View } from 'ol';
import { Role, ViewMode } from 'types/app.types';
import { useLocation } from 'react-router-dom';
import ProductsContext from '../ProductsContext';
import { Projection } from '../../configs/projections';
import { parseEnvSupervisorProduct } from '../../utils';

/*
 * The MainApp context manages the different modes that the App support
 * It is the top level wrapper
 */

interface MainAppContextValues {
    crossSectionMode: boolean,
    playbackMode: boolean,
    realTimeMode: boolean,
    setCrossSectionMode: Dispatch<SetStateAction<boolean>>,
    setRealTimeMode: Dispatch<SetStateAction<boolean>>,
    setPlaybackMode: Dispatch<SetStateAction<boolean>>,
    setSyncLayers: Dispatch<SetStateAction<boolean>>,
    setViewMode: Dispatch<SetStateAction<ViewMode>>
    syncLayers: boolean,
    viewMode: ViewMode,
    latestView: MutableRefObject<View[]>
    displayAlarms: boolean,
    setDisplayAlarms: Dispatch<SetStateAction<boolean>>,
    adsbIsEnabled: boolean,
    setAdsbIsEnabled: Dispatch<SetStateAction<boolean>>,
    currentProjection: Projection,
    setCurrentProjection: Dispatch<SetStateAction<Projection>>,
    role: Role,
}

const MainAppContext = createContext<MainAppContextValues>({
  crossSectionMode: false,
  playbackMode: false,
  realTimeMode: false,
  setCrossSectionMode: () => {},
  setRealTimeMode: () => {},
  setPlaybackMode: () => {},
  setSyncLayers: () => {},
  setViewMode: () => {},
  syncLayers: false,
  viewMode: 'layer',
  latestView: { current: [] },
  displayAlarms: true,
  setDisplayAlarms: () => {},
  adsbIsEnabled: false,
  setAdsbIsEnabled: () => {},
  currentProjection: Projection.PSEUDO_MERCATOR,
  setCurrentProjection: () => {},
  role: Role.METEO,
});

interface MainAppProviderProps {
    children: ReactNode,
  }

// eslint-disable-next-line max-len, no-underscore-dangle
const fixedProducts = parseEnvSupervisorProduct((window as any)._env_.REACT_APP_SUPERVISOR_PRODUCTS);

function MainAppProvider({ children } : MainAppProviderProps) {
  const location = useLocation();
  const [role, setRole] = useState<Role>(Role.METEO);
  const [viewMode, setViewMode] = useState<ViewMode>('layer');
  const [syncLayers, setSyncLayers] = useState<boolean>(false);
  const [playbackMode, setPlaybackMode] = useState(false);
  const [crossSectionMode, setCrossSectionMode] = useState(false);
  const [realTimeMode, setRealTimeMode] = useState(true);
  const [displayAlarms, setDisplayAlarms] = useState(false);
  const [adsbIsEnabled, setAdsbIsEnabled] = useState(true);
  const latestView = useRef<View[]>([]);
  const { selectedProducts, setSelectedProducts } = useContext(ProductsContext);
  const [
    currentProjection,
    setCurrentProjection] = useState<Projection>(Projection.PSEUDO_MERCATOR);

  const value = useMemo(() => ({
    crossSectionMode,
    playbackMode,
    realTimeMode,
    setCrossSectionMode,
    setPlaybackMode,
    setRealTimeMode,
    setSyncLayers,
    setViewMode,
    syncLayers,
    viewMode,
    latestView,
    displayAlarms,
    setDisplayAlarms,
    adsbIsEnabled,
    setAdsbIsEnabled,
    currentProjection,
    setCurrentProjection,
    role,
  }), [crossSectionMode, displayAlarms, playbackMode, realTimeMode,
    syncLayers, viewMode, adsbIsEnabled, currentProjection, setCurrentProjection, role]);

  useEffect(() => {
    if (selectedProducts.length <= 1 && viewMode === 'tile' && role !== Role.SUPERVISOR) {
      setViewMode('layer');
    }
  }, [role, selectedProducts, viewMode]);

  useEffect(() => {
    if (viewMode === 'tile') {
      setPlaybackMode(false);
      setSyncLayers(true);
    }
    if (viewMode === 'layer') {
      setPlaybackMode(false);
      setSyncLayers(false);
    }
  }, [viewMode]);

  useEffect(() => {
    if (location.pathname.includes('supervisor') && fixedProducts) {
      setRole(Role.SUPERVISOR);
      setViewMode('tile');
    }
  }, [location.pathname, setSelectedProducts]);

  return (
    <MainAppContext.Provider value={value}>
      {children}
    </MainAppContext.Provider>
  );
}

export { MainAppProvider };
export default MainAppContext;
